import { useState } from "preact/hooks";

import { ApiError, sendTestPush } from "../lib/api";
import type { PushTestOut } from "../types";

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/** Plain-language result line for a finished test. Exported for tests. */
export function testResultMessage(result: PushTestOut): { text: string; ok: boolean } {
  const { device_count: total, delivered_count: delivered } = result;
  if (delivered === 0) {
    return {
      text: "Couldn't reach your devices. Try turning Daily reminders off and on again.",
      ok: false,
    };
  }
  if (delivered < total) {
    const missed = total - delivered;
    return {
      text: `Sent to ${delivered} of ${plural(total, "device")} — ${missed} couldn't be reached.`,
      ok: true,
    };
  }
  return {
    text: `Sent to ${plural(total, "device")}. It should pop up in a few seconds.`,
    ok: true,
  };
}

/** Plain-language line for a test that never got sent. Exported for tests. */
export function testErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.isNetworkError) return "You're offline — try again when you have signal.";
    if (err.status === 409) {
      return "No devices are set up for reminders yet. Turn on Daily reminders first.";
    }
    if (err.status === 429) return "That's a few tests in a row — wait a minute and try again.";
    if (err.status === 503) return "Reminders aren't turned on for this farm yet.";
  }
  return "Couldn't send a test right now — try again.";
}

/**
 * "Send me a test notification" — shown under the Daily reminders switch
 * once this device is subscribed. Calls `POST /api/v1/me/push/test`, which
 * only ever reaches the person's OWN devices, and says how it went inline.
 */
export function TestNotificationButton() {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);

  async function handleClick() {
    if (busy) return;
    setBusy(true);
    setMessage(null);
    try {
      setMessage(testResultMessage(await sendTestPush()));
    } catch (err) {
      setMessage({ text: testErrorMessage(err), ok: false });
    } finally {
      setBusy(false);
    }
  }

  return (
    <span class="reminder-test">
      <button
        type="button"
        class="secondary-btn reminder-test-btn"
        disabled={busy}
        onClick={() => void handleClick()}
      >
        {busy ? "Sending…" : "Send me a test notification"}
      </button>
      {message ? (
        <span
          class={message.ok ? "reminder-test-result" : "reminder-error-hint"}
          role={message.ok ? "status" : "alert"}
        >
          {message.text}
        </span>
      ) : null}
    </span>
  );
}
